import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { actions as actionsUserCards } from 'appRedux/modules/UserCards';
import { values as _values } from 'lodash';
import { Loading, Modal } from 'components';
import { FunPointForm, TapPayForm } from './components';
import { actions as actionsModal } from 'appRedux/modules/Modal';

const removeCardConfirmModalID = 'remove-card-modal';

/** ---------------------------------------------------------------------------------------------
 * Props
 * @type {string} source Source of the credit card form request
 *  - order: Payment page
 *  - profile: Profile page
 *
 * @type {Object} paymentConfigs Payment configs
 *  @property {string} atm_type ATM payment type
 *  @property {number} close If credit card payment available
 *   - 0: open
 *   - 1: close
 *  @property {string} credit_period_type Credit period payment type
 *  @property {string} credit_type Credit payment type
 *   - funpoint: FunPoint
 *   - tappay: TapPay
 *  @property {string} cvs_type CVS payment type
 *
 * @type {boolean} useCreditCard If user chooses to use credit card as payment method
 */
const CreditCardForm = ({
	source = 'order',
	paymentConfigs = {},
	useCreditCard = true,
}) => {
	/** ---------------------------------------------------------------------------------------------
	 * Basic
	 */
	const dispatch = useDispatch();

	/** ---------------------------------------------------------------------------------------------
	 * Redux
	 * @type {object} card_bound Card bound
	 *  - false: Default value before initialized
	 *  - null: No card bound
	 *  - object: Card bound
	 *   @property {string} bindCardID Bind card ID
	 *   @property {string} bincode Bincode (first six digits)
	 *   @property {string} lastfour Last four digits
	 *   @property {string} trade_type Trade type
	 *    - funpoint: FunPoint
	 *    - tappay: TapPay
	 */
	const card_bound = useSelector(state => state.UserCards.card_bound);

	/** ---------------------------------------------------------------------------------------------
	 * State
	 * @type {object}    cardBound      Card bound
	 *  - false:  default value before initialized
	 *  - null:   no card bound or different trade type
	 *  - object: card bound and same trade type
	 *
	 * @type {boolean}   loading        If data is loading
	 */
	const [cardBound, setCardBound] = useState(false);
	const [loading, setLoading] = useState(false);

	/** ---------------------------------------------------------------------------------------------
	 * Variable
	 * @type {array} creditTypes Credit types (credit_period_type, credit_type)
	 *
	 * @type {boolean} isFunPoint If the credit card payment type is FunPoint
	 *
	 * @type {boolean} isTapPay If the credit card payment type is TapPay
	 *
	 * @type {string} displayFunPoint Display FunPoint form
	 * @type {string} displayTapPay Display TapPay form
	 *  - block: Display
	 *  - none: Hide
	 */
	const creditTypes = _values(paymentConfigs).filter(
		item => item && item.includes('credit')
	);
	const isFunPoint =
		creditTypes.filter(item => item.includes('funpoint')).length > 0;
	const isTapPay =
		creditTypes.filter(item => item.includes('tappay')).length > 0;
	const displayFunPoint = isFunPoint ? 'block' : 'none';
	const displayTapPay = isTapPay ? 'block' : 'none';

	/** ---------------------------------------------------------------------------------------------
	 * Function
	 * @type {Function} removeCard Soft delete the card
	 */
	const removeCard = () => {
		setLoading(true);

		// const tradeType = cardBound?.trade_type;

		// if (tradeType === 'funpoint') {
			dispatch(
				actionsUserCards.removeUserCardRequest()
			);
		// } else if (tradeType === 'tappay') {
		// 	dispatch(
		// 		actionsUserCards.removeUserCardRequest({
		// 			cardKey: cardBound.card_key,
		// 			cardToken: cardBound.card_token,
		// 		})
		// 	);
		// }

		dispatch(actionsModal.toggleModal(removeCardConfirmModalID, false));
	};

	const openConfirmModal = () => {
		dispatch(actionsModal.toggleModal(removeCardConfirmModalID, true));
	};

	/** ---------------------------------------------------------------------------------------------
	 * useEffect
	 */
	useEffect(() => {
		// Get card bound before by user
		if (card_bound === false) {
			if (loading) {
				return;
			}

			setLoading(true);
			// Get the credit card bound request
			dispatch(actionsUserCards.getUserCardRequest());
		} else {
			let cardUpdated = null;

			if (card_bound?.trade_type) {
				const tradeType = card_bound?.trade_type;

				if (
					(isFunPoint && tradeType === 'funpoint') ||
					(isTapPay && tradeType === 'tappay')
				) {
					cardUpdated = card_bound;
				}
			}

			setCardBound(cardUpdated);
			setLoading(false);
		}
	}, [loading, card_bound, dispatch, isFunPoint, isTapPay]);

	// ---------------------------------------------------------------------------------------------

	return useCreditCard ? (
		<div
			className={`credit-card-form ua-form ${
				useCreditCard ? 'min-height-140' : 'height-0'
			}`}
		>
			{useCreditCard && source === 'order' && (
				<hr className='w-100 margin-vertical-15' />
			)}

			{((useCreditCard &&
				!(displayFunPoint === 'block' || displayTapPay === 'block')) ||
				loading) && <Loading />}

			{cardBound === null && (
				<div className='margin-top-30 text-center'>
					{isFunPoint 
					? (
						<h3 className='margin-vertical-15 txt-grey'>
							目前尚不支援國泰 MasterCard 及陽信 JCB 信用卡
							<br />
							建議使用其他卡或其他付款方式
						</h3>
					)
					: null}

					<h3>
						<span>
							綁定信用卡將會試刷 {isFunPoint ? 6 : 1} 元取得授權，
						</span>

						<span className='txt-orange txt-semibold'>
							成功後即退還
						</span>
					</h3>
				</div>
			)}

			{cardBound && (
				<>
					<h1 className='bound-card margin-top-50 text-center'>
						{cardBound.bincode}******{cardBound.lastfour}
					</h1>

					<div className='btn-container'>
						<button
							type='button'
							className='cancel ua-btn outline'
							onClick={openConfirmModal}
						>
							解除後重新綁定
						</button>
					</div>

					<Modal
						id={removeCardConfirmModalID}
						title={
							<>
								<span className='txt-orange txt-semibold'>
									確認解除綁定信用卡
								</span>
							</>
						}
						html={
							<div className='h-100 w-100 d-flex flex-column justify-content-center align-items-center'>
								<div className='w-100 d-flex justify-content-center'>
									<i
										className='far fa-exclamation-circle'
										style={{
											fontSize: '80px',
										}}
									></i>
								</div>
								<div className='margin-top-20'>
									<p className='text-center'>
										你即將解除綁定此信用卡{' '}
										<span className='txt-orange txt-semibold'>
											{cardBound?.bincode}
											****** {cardBound?.lastfour}。
										</span>
									</p>
									<p className='text-center txt-semibold'>
										此操作無法復原，是否確認？
									</p>
								</div>
							</div>
						}
						leaveTxt='關閉'
						checkBtn={
							<button
								type='button'
								className='submit ua-btn default margin-left-15'
								onClick={removeCard}
							>
								確定
							</button>
						}
					/>
				</>
			)}
			
			<FunPointForm
				display={displayFunPoint}
				source={source}
				cardBound={cardBound}
			/>

			<TapPayForm
				display={displayTapPay}
				source={source}
				cardBound={cardBound}
			/>

			{cardBound === null && (
				<p className='txt-orange txt-semibold text-center'>
					信用卡綁定後，僅適用於新購商品及其後續扣款，現有訂閱不受影響。
				</p>
			)}
		</div>
	) : null;
};

export { CreditCardForm };
